import React, { useCallback, useMemo } from 'react';
import { useAgencyDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/AgencyDashboard/store/agencyDashboard.store';
import { DataTable } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/DataTable';
import { DataTableColumn } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.types';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.styled';
import { formatDashboardNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/formatDashboardNumber';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';

const COLUMNS: DataTableColumn[] = [
  { key: 'scheduler', label: 'Scheduler' },
  { key: 'location', label: 'Location' },
  { key: 'shiftCount', label: 'Shifts', align: 'right', noWrap: true },
  { key: 'hours', label: 'Hours', align: 'right', noWrap: true },
  { key: 'averageLeadHours', label: 'Avg lead', align: 'right', noWrap: true },
];

export const ShiftsSentBySchedulerTable: React.FC = () => {
  const data = useAgencyDashboardStore((s) => s.shiftsSentByScheduler);
  const currentPage = useAgencyDashboardStore((s) => s.shiftsSentBySchedulerPage);
  const totalPages = useAgencyDashboardStore((s) => s.shiftsSentBySchedulerTotalPages);
  const isLoading = useAgencyDashboardStore((s) => s.shiftsSentBySchedulerLoading);
  const hasError = useAgencyDashboardStore((s) => s.shiftsSentBySchedulerError);
  const fetchShiftsSentByScheduler = useAgencyDashboardStore((s) => s.fetchShiftsSentByScheduler);

  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);

  const handlePageChange = useCallback(
    (page: number) => {
      fetchShiftsSentByScheduler(page, dateRange, locationIds, positions, employmentTypes);
    },
    [fetchShiftsSentByScheduler, dateRange, locationIds, positions, employmentTypes],
  );

  const rows = useMemo(
    () =>
      data?.map((d) => ({
        scheduler: d.scheduler,
        location: d.location,
        shiftCount: d.shiftCount.toLocaleString(),
        hours: `${formatDashboardNumber(d.hours)}h`,
        averageLeadHours: `${formatDashboardNumber(d.averageLeadHours)}h`,
      })) ?? [],
    [data],
  );

  if (hasError) {
    return <EmptyStateContainer>Failed to load shifts sent to agency data</EmptyStateContainer>;
  }

  if (!data && !isLoading) {
    return (
      <EmptyStateContainer>
        No agency scheduler data available for the selected filters.
      </EmptyStateContainer>
    );
  }

  return (
    <DataTable
      columns={COLUMNS}
      rows={rows}
      ariaLabel="Shifts sent to agency by scheduler and location table"
      serverPagination={{
        currentPage,
        totalPages,
        onPageChange: handlePageChange,
        isLoading,
      }}
    />
  );
};
